import React, { useCallback, useEffect, useRef, useState } from "react";
import CommonErrorBanner from "../../../../components/common/CommonErrorBanner";

export type ProjectBannerPlacement = "summary" | "detail";

type ProjectBannerVariant = "Success" | "Error";

type ProjectBannerProps = {
  show: boolean;
  message: string;
  bannerRef: React.MutableRefObject<HTMLDivElement | null>;
  placement?: ProjectBannerPlacement;
};

type UseProjectBannerReturn = {
  bannerRef: React.MutableRefObject<HTMLDivElement | null>;
  isVisible: boolean;
  message: string;
  placement: ProjectBannerPlacement;
  showBanner: (nextMessage: string, nextPlacement?: ProjectBannerPlacement) => void;
  hideBanner: () => void;
};

const AUTO_HIDE_MS = 5000;

const ProjectBanner = ({
  show,
  message,
  bannerRef,
  placement = "detail",
  variant,
}: ProjectBannerProps & { variant: ProjectBannerVariant }) => {
  if (!show) return null;

  return (
    <div
      ref={bannerRef}
      tabIndex={-1}
      data-placement={placement}
      // Keep tab headers visible when auto-scrolling banners into view.
      style={{ scrollMarginTop: "120px" }}
    >
      <CommonErrorBanner
        heading={message}
        messageInfo=""
        variant={variant}
      />
    </div>
  );
};

export const ProjectSuccessBanner = (props: ProjectBannerProps) => (
  <ProjectBanner {...props} variant="Success" />
);

export const ProjectFailureBanner = (props: ProjectBannerProps) => (
  <ProjectBanner {...props} variant="Error" />
);

export const useProjectBanner = (
  initialMessage: string,
  initialPlacement: ProjectBannerPlacement = "detail",
): UseProjectBannerReturn => {
  const bannerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [message, setMessage] = useState(initialMessage);
  const [placement, setPlacement] = useState<ProjectBannerPlacement>(initialPlacement);

  const hideBanner = useCallback(() => {
    setIsVisible(false);
  }, []);

  const showBanner = useCallback(
    (nextMessage: string, nextPlacement: ProjectBannerPlacement = initialPlacement) => {
      setMessage(nextMessage);
      setPlacement(nextPlacement);
      setIsVisible(true);
    },
    [initialPlacement],
  );

  useEffect(() => {
    if (!isVisible) return;

    const scrollId = setTimeout(() => {
      bannerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      globalThis.setTimeout(() => {
        bannerRef.current?.focus({ preventScroll: true });
      }, 150);
    }, 50);
    const hideId = setTimeout(() => {
      setIsVisible(false);
    }, AUTO_HIDE_MS);

    return () => {
      clearTimeout(scrollId);
      clearTimeout(hideId);
    };
  }, [isVisible]);

  return {
    bannerRef,
    isVisible,
    message,
    placement,
    showBanner,
    hideBanner,
  };
};